<!--
    Página 404 "Habitación no encontrada" (adaptada de
    primeras_tareas/errno_404). Se muestra en cualquier dirección que no
    coincide con ninguna ruta (ruta comodín '*' al final de router.js).
    Mantiene el header del sitio. Textos en las traducciones: not_found.*

    Variante "espacio" (<NotFound variante="espacio" />): la misma página, con
    403, textos not_found.space_*, botón a Mis espacios y sin Sophi. La usa el
    editor cuando el alojamiento no existe o no es del usuario.

    Liviana a propósito: las auras son fijas (sin blur, sin animar, sin seguir
    al cursor), el latido del botón solo anima transform/opacity y nada usa
    backdrop-filter.

    - La puerta 404: al hacer clic (o Enter / espacio) golpea y se abre.
    - Auras que siguen levemente al cursor.
    - Sophi: a los segundos aparece el lanzador escribiendo; al hacer clic
      o al abrir la puerta se despliega el chat con el enlace al formulario.

    Las clases llevan prefijo propio (e404-btn, e404-container…) para no
    chocar con las de Bootstrap que el sitio carga de forma global.
-->
<template>
    <div class="nf">
        <main class="e404" ref="hero">
            <!-- Marca de agua: el número grande en contorno, detrás de todo -->
            <span class="e404__watermark" aria-hidden="true">{{ codigo }}</span>

            <!-- Auras de marca, fijas -->
            <span class="e404__aura e404__aura--a" aria-hidden="true"></span>
            <span class="e404__aura e404__aura--b" aria-hidden="true"></span>
            <span class="e404__aura e404__aura--c" aria-hidden="true"></span>

            <div class="e404-container e404__grid">

                <!-- Columna de texto -->
                <div class="e404__copy">
                    <h1 class="e404__title e404-reveal" :class="{ 'in': revealed }">
                        {{ $t(k('title')) }}
                        <span class="e404__accent">{{ $t(k('title_accent')) }}</span>
                    </h1>

                    <!-- Texto fijo de las traducciones (lleva <b>), no datos del usuario -->
                    <p class="e404__desc e404-reveal" :class="{ 'in': revealed }" style="--delay: 80ms" v-html="$t(k('desc'))"></p>

                    <div class="e404__cta e404-reveal" :class="{ 'in': revealed }" style="--delay: 160ms">
                        <router-link class="e404-btn e404-btn--dark e404-btn--llama" :to="esEspacio ? { name: 'my-accommodations' } : '/'">
                            {{ $t(k('back_home')) }}
                            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M7 17 17 7M9 7h8v8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                        </router-link>
                    </div>
                </div>

                <!-- Escena: la puerta de la habitación 404 -->
                <div class="e404__stage e404-reveal" :class="{ 'in': revealed }" style="--delay: 200ms">
                    <figure class="scene" :aria-label="$t('not_found.scene_label')">
                        <div
                            class="door-scene"
                            :class="{ 'is-open': doorOpen, 'is-knocking': knocking }"
                            role="button"
                            tabindex="0"
                            :aria-label="$t('not_found.door_label')"
                            :aria-pressed="doorOpen ? 'true' : 'false'"
                            @click="onDoorClick"
                            @keydown.enter.prevent="onDoorClick"
                            @keydown.space.prevent="onDoorClick"
                        >
                            <div class="wall">
                                <!-- Número de la habitación -->
                                <span class="plaque" aria-hidden="true">
                                    <span class="plaque__num">{{ codigo }}</span>
                                    <span class="plaque__sub">{{ $t('not_found.room') }}</span>
                                </span>

                                <!-- Marco y hoja -->
                                <div class="frame">
                                    <!-- Lo que hay detrás: nada, iluminado -->
                                    <div class="void" aria-hidden="true">
                                        <span class="void__glow"></span>
                                        <span class="void__text" v-html="$t('not_found.void_text')"></span>
                                    </div>

                                    <div class="door">
                                        <span class="door__panel door__panel--top"></span>
                                        <span class="door__panel door__panel--bottom"></span>
                                        <span class="door__peephole"></span>
                                        <span class="door__handle"></span>

                                        <!-- Cartel que se balancea colgado del picaporte -->
                                        <span class="hanger" aria-hidden="true">
                                            <span class="hanger__ring"></span>
                                            <span class="hanger__card">
                                                <b>{{ $t('not_found.sign_1') }}</b>
                                                <b>{{ $t('not_found.sign_2') }}</b>
                                                <small>{{ $t('not_found.sign_3') }}</small>
                                            </span>
                                        </span>
                                    </div>
                                </div>

                                <!-- Felpudo -->
                                <span class="mat" aria-hidden="true">{{ $t('not_found.mat') }}</span>
                            </div>

                            <!-- "toc toc" al hacer clic -->
                            <span class="knock" aria-hidden="true"><i>{{ $t('not_found.knock') }}</i><i>{{ $t('not_found.knock') }}</i></span>
                        </div>
                    </figure>
                </div>

            </div>
        </main>

        <!-- Sophi: lanzador compacto (avatar + avance del primer mensaje) -->
        <button
            v-show="launcherShown"
            class="sophi-launcher"
            :class="{ 'is-in': launcherIn, 'has-unread': unread }"
            type="button"
            :aria-label="$t('not_found.sophi_open')"
            @click="openPanel"
        >
            <span v-if="teaser" class="sophi-launcher__teaser" aria-live="polite">
                <template v-if="teaser === 'typing'"><i></i><i></i><i></i></template>
                <template v-else>{{ $t('not_found.' + teaser) }}</template>
            </span>
            <span class="sophi-launcher__btn">
                <span class="sophi__avatar" aria-hidden="true">S</span>
                <b>Sophi</b>
                <em class="sophi-launcher__badge" aria-hidden="true">1</em>
            </span>
        </button>

        <!-- Panel de chat -->
        <aside
            v-show="panelShown"
            class="sophi"
            :class="{ 'is-in': panelIn, 'has-cta': hasCta }"
            aria-live="polite"
            :aria-label="$t('not_found.sophi_chat')"
        >
            <header class="sophi__bar">
                <span class="sophi__avatar" aria-hidden="true">S</span>
                <span class="sophi__who"><b>Sophi</b><small>{{ $t('not_found.sophi_online') }}</small></span>
                <button class="sophi__close" type="button" :aria-label="$t('not_found.sophi_close')" @click="closePanel">
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"/></svg>
                </button>
            </header>
            <div class="sophi__log">
                <div v-for="m in messages" :key="m.id" class="msg" :class="{ 'msg--typing': m.typing }">
                    <template v-if="m.typing"><i></i><i></i><i></i></template>
                    <template v-else><span v-html="$t('not_found.' + m.key)"></span><small>{{ m.time }}</small></template>
                </div>
            </div>
            <router-link class="sophi__cta e404-btn e404-btn--dark" to="/formulario">{{ $t('not_found.cta') }}</router-link>
        </aside>
    </div>
</template>

<script>
const FONT_HREF = 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap';

export default {
    name: 'NotFound',
    props: {
        // 'ruta' = dirección que no existe (la 404 de siempre).
        // 'espacio' = alojamiento que no existe o no es del usuario (editor).
        variante: { type: String, default: 'ruta' }
    },
    computed: {
        esEspacio() { return this.variante === 'espacio'; },
        codigo() { return this.esEspacio ? '403' : '404'; }
    },
    data() {
        return {
            revealed: false,
            doorOpen: false,
            knocking: false,
            launcherShown: false,
            launcherIn: false,
            teaser: '',          // '' | 'typing' | clave de not_found.* (se traduce al pintar)
            unread: false,
            panelShown: false,
            panelIn: false,
            panelOpen: false,
            hasCta: false,
            scriptRan: false,
            messages: []
        };
    },
    mounted() {
        this.timers = [];
        this.msgSeq = 0;
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.setupHead();
        this.$watch(() => this.$i18n.locale, () => { document.title = this.$t(this.k('meta_title')); });

        // El hero ocupa lo que queda del visor debajo del header del sitio.
        this.fitToViewport = () => {
            const top = this.$refs.hero.getBoundingClientRect().top + window.scrollY;
            this.$el.style.setProperty('--e404-header', Math.max(0, Math.round(top)) + 'px');
        };
        this.fitToViewport();
        window.addEventListener('resize', this.fitToViewport, { passive: true });

        // Entrada: espera a que se pinte el estado inicial para que la transición se vea.
        requestAnimationFrame(() => requestAnimationFrame(() => { this.revealed = true; }));

        // Llegada de Sophi (solo en la 404 de rutas: en "espacio" el que llega es
        // un anfitrión, no alguien buscando alojamiento).
        if (!this.esEspacio) this.wait(this.reduceMotion ? 300 : 2200, () => {
            this.showLauncher(true);
            this.wait(this.reduceMotion ? 0 : 1300, () => {
                this.teaser = 'teaser';
                this.unread = true;
            });
        });
    },
    beforeDestroy() {
        this.timers.forEach(clearTimeout);
        window.removeEventListener('resize', this.fitToViewport);
        this.restoreHead();
    },
    methods: {
        wait(ms, fn) { this.timers.push(setTimeout(fn, ms)); },
        // Clave de traducción según la variante (not_found.x o not_found.space_x).
        k(clave) { return 'not_found.' + (this.esEspacio ? 'space_' : '') + clave; },

        // Título propio y noindex mientras se ve la 404.
        setupHead() {
            this.prevTitle = document.title;
            document.title = this.$t(this.k('meta_title'));
            this.robots = document.querySelector('meta[name="robots"]');
            this.prevRobots = this.robots ? this.robots.getAttribute('content') : null;
            if (!this.robots) {
                this.robots = document.createElement('meta');
                this.robots.setAttribute('name', 'robots');
                document.head.appendChild(this.robots);
                this.createdRobots = true;
            }
            this.robots.setAttribute('content', 'noindex');
            // Misma fuente que el diseño original (solo se carga una vez).
            if (!document.querySelector('link[data-e404-font]')) {
                const link = document.createElement('link');
                link.rel = 'stylesheet';
                link.href = FONT_HREF;
                link.setAttribute('data-e404-font', '');
                document.head.appendChild(link);
            }
        },
        restoreHead() {
            document.title = this.prevTitle;
            if (!this.robots) return;
            if (this.createdRobots) this.robots.parentNode.removeChild(this.robots);
            else this.robots.setAttribute('content', this.prevRobots);
        },

        // ---------- La puerta ----------
        onDoorClick() {
            if (this.reduceMotion) { this.toggleDoor(); return; }
            this.knock();
            // Golpea, espera un instante como si alguien fuera a atender, y abre.
            this.wait(520, this.toggleDoor);
        },
        knock() {
            // Reinicia la animación si se toca dos veces seguidas.
            this.knocking = false;
            this.$nextTick(() => {
                void this.$el.offsetWidth;
                this.knocking = true;
                clearTimeout(this.knockTimer);
                this.knockTimer = setTimeout(() => { this.knocking = false; }, 700);
                this.timers.push(this.knockTimer);
            });
        },
        toggleDoor() {
            this.doorOpen = !this.doorOpen;
            if (this.doorOpen) this.onDoorOpen();
        },
        // La puerta abierta llama a Sophi.
        onDoorOpen() {
            const first = !this.scriptRan;
            if (!this.panelOpen) this.openPanel();
            // Si el guion acaba de empezar, la broma espera a que termine.
            this.wait(first && !this.reduceMotion ? 3600 : 0, () => {
                this.sophiSay('msg_joke');
            });
        },

        // ---------- Sophi ----------
        hora() {
            const d = new Date();
            const h = d.getHours(), m = d.getMinutes();
            return (h < 10 ? '0' : '') + h + ':' + (m < 10 ? '0' : '') + m;
        },
        // Escribe un mensaje: primero los puntitos, después el texto. Se guarda
        // la clave de traducción para que el chat cambie si se cambia el idioma.
        sophiSay(key, onDone) {
            const msg = { id: ++this.msgSeq, typing: true, key: '', time: '' };
            this.messages.push(msg);
            const len = String(this.$t('not_found.' + key)).length;
            const delay = this.reduceMotion ? 0 : Math.min(1600, 500 + len * 18);
            this.wait(delay, () => {
                msg.typing = false;
                msg.key = key;
                msg.time = this.hora();
                if (onDone) onDone();
            });
        },
        runScript() {
            if (this.scriptRan) return;
            this.scriptRan = true;
            this.sophiSay('msg_hello', () => {
                this.wait(this.reduceMotion ? 0 : 900, () => {
                    this.sophiSay('msg_offer', () => {
                        this.hasCta = true;
                    });
                });
            });
        },
        showLauncher(withTeaser) {
            this.launcherShown = true;
            if (withTeaser) this.teaser = 'typing';
            else this.teaser = '';
            requestAnimationFrame(() => { this.launcherIn = true; });
        },
        hideLauncher() {
            this.launcherIn = false;
            this.wait(450, () => { if (!this.launcherIn) this.launcherShown = false; });
        },
        openPanel() {
            if (this.panelOpen) return;
            this.panelOpen = true;
            this.hideLauncher();
            this.unread = false;
            this.panelShown = true;
            requestAnimationFrame(() => { this.panelIn = true; });
            this.runScript();
        },
        closePanel() {
            if (!this.panelOpen) return;
            this.panelOpen = false;
            this.panelIn = false;
            this.wait(450, () => {
                if (this.panelOpen) return;
                this.panelShown = false;
                this.showLauncher(false);
            });
        }
    }
};
</script>

<style scoped>
/* ---------- Tokens (copiados del diseño) ---------- */
.nf {
    --ink: #080b0f;
    --muted: #4b5b63;
    --bg: #ffffff;
    --surface: #f5f5f5;
    --line: #e2e2e2;
    --line-strong: #c7c7c7;
    --accent: #f2681f;
    --grad-a: #6d3ce0;
    --grad-b: #b8399f;
    --grad-c: #f2681f;
    --btn-a: #c1220b;
    --btn-b: #e64f14;
    --btn-c: var(--accent);
    --chat-bg: #e7dcd0;
    --chat-bar: #202c33;
    --chat-in: #ffffff;
    --radius-pill: 999px;
    --maxw: 1200px;
    --gutter: 24px;
    --ease: cubic-bezier(.22, .61, .36, 1);
    --shadow: 0 18px 50px -20px rgba(8, 11, 15, .28);
    /* Madera de la puerta: cálida y muy bajada, para que brille el picaporte naranja */
    --wood-1: #f3e9df;
    --wood-2: #e6d6c6;
    --wood-line: #d4c0ac;
    /* Lo que ocupa el header del sitio arriba del hero. Lo mide el JS
       (fitToViewport); 145 px es lo que mide hoy en escritorio. */
    --e404-header: 145px;

    font-family: "Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    font-size: 16px;
    line-height: 1.55;
    color: var(--ink);
    background: var(--bg);
    -webkit-font-smoothing: antialiased;
    overflow-x: clip;
}

/* Reset local (Bootstrap pone márgenes y pesos propios en h1, p, figure, b…) */
.nf, .nf * { box-sizing: border-box; margin: 0; padding: 0; }
.nf a { color: inherit; text-decoration: none; }
.nf button { font: inherit; cursor: pointer; border: none; background: none; color: inherit; border-radius: 0; }
.nf b { font-weight: 700; }
.nf svg { max-width: 100%; }

/* ---------- Layout ---------- */
.e404-container {
    width: 100%;
    max-width: var(--maxw);
    margin-inline: auto;
    padding-inline: var(--gutter);
}

/* ---------- Botones (idénticos al diseño) ---------- */
.e404-btn {
    --pad-y: 13px;
    --pad-x: 22px;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: var(--pad-y) var(--pad-x);
    border-radius: var(--radius-pill);
    font-weight: 600;
    font-size: 15px;
    line-height: 1;
    transition: transform .25s var(--ease), scale .3s var(--ease), filter .3s var(--ease),
                background .25s var(--ease), background-position .5s var(--ease), box-shadow .3s var(--ease),
                color .25s var(--ease), border-color .25s var(--ease);
    will-change: transform;
}
.e404-btn svg { width: 16px; height: 16px; transition: transform .3s var(--ease); }
.e404-btn:hover { transform: translateY(-2px); text-decoration: none; }
.e404-btn:hover svg { transform: translate(3px, -3px); }

.nf .e404-btn--dark,
.nf .e404-btn--dark:hover { color: #fff; }
.e404-btn--dark {
    background-image: linear-gradient(115deg, var(--btn-a) 0%, var(--btn-b) 25%, var(--btn-c) 50%, var(--btn-b) 75%, var(--btn-a) 100%);
    background-size: 200% 100%;
    background-position: 0% 50%;
    box-shadow: 0 9px 24px -12px rgba(230, 79, 20, .9), inset 0 1px 0 rgba(255, 255, 255, .26);
}
.e404-btn--dark:hover {
    background-position: 100% 50%;
    box-shadow: 0 16px 34px -12px rgba(230, 79, 20, 1), inset 0 1px 0 rgba(255, 255, 255, .34);
}

.e404-btn--llama { position: relative; animation: btn-llama-latido 2.4s var(--ease) infinite; }
.e404-btn--llama::after {
    content: "";
    position: absolute;
    top: 0; right: 0; bottom: 0; left: 0;
    border-radius: inherit;
    pointer-events: none;
    /* El aro es un borde fijo que crece y se desvanece: solo transform/opacity,
       que no obligan a repintar (antes se animaba box-shadow + filter). */
    box-shadow: 0 0 0 2px rgba(230, 79, 20, .55);
    opacity: 0;
    animation: btn-llama-aro 2.4s var(--ease) infinite;
}
.e404-btn--llama:hover,
.e404-btn--llama:hover::after,
.e404-btn--llama:focus-visible,
.e404-btn--llama:focus-visible::after { animation: none; }

@keyframes btn-llama-aro {
    0%        { transform: scale(1);         opacity: .9; }
    16%       { transform: scale(1.12, 1.5); opacity: 0; }
    17%       { transform: scale(1);         opacity: .75; }
    33%, 100% { transform: scale(1.12, 1.5); opacity: 0; }
}
@keyframes btn-llama-latido {
    0%        { scale: 1; }
    8%        { scale: 1.06; }
    16%       { scale: 1; }
    25%       { scale: 1.045; }
    33%, 100% { scale: 1; }
}

/* =========================================================
   HERO 404
   ========================================================= */
.e404 {
    position: relative;
    isolation: isolate;
    /* Recorta las auras y la marca de agua (si no, alargan la página);
       el contenido no se corta porque el alto es mínimo, no fijo. */
    overflow: hidden;
    /* Pantalla completa: en escritorio ocupa el visor exacto; en móvil crece si hace falta. */
    min-height: calc(100vh - var(--e404-header));
    min-height: calc(100dvh - var(--e404-header));
    display: flex;
    align-items: center;
    background: linear-gradient(180deg, #ffffff 0%, #f6f4fb 60%, #ffffff 100%);
}

/* Auras de marca, fijas: el radial-gradient ya es suave, no hace falta blur
   (antes llevaban blur(40px), respiraban sin parar y seguían al cursor). */
.e404__aura {
    position: absolute;
    z-index: -1;
    border-radius: 50%;
    pointer-events: none;
}
.e404__aura--a { left: -8%; top: 10%; width: 46vw; height: 46vw; background: radial-gradient(closest-side, rgba(109, 60, 224, .26), transparent 70%); }
.e404__aura--b { right: -10%; top: -6%; width: 42vw; height: 42vw; background: radial-gradient(closest-side, rgba(184, 57, 159, .16), transparent 70%); animation-delay: -5s; }
.e404__aura--c { right: 18%; bottom: -14%; width: 40vw; height: 40vw; background: radial-gradient(closest-side, rgba(242, 104, 31, .14), transparent 70%); animation-delay: -9s; }

/* Número gigante en contorno, casi invisible */
.e404__watermark {
    position: absolute;
    z-index: -1;
    right: -1vw;
    bottom: -2vw;
    font-size: clamp(180px, 24vw, 380px);
    font-weight: 800;
    line-height: 1;
    letter-spacing: -.06em;
    color: transparent;
    -webkit-text-stroke: 1.5px rgba(8, 11, 15, .06);
    pointer-events: none;
    user-select: none;
}

.e404__copy, .e404__stage { min-width: 0; }
/* El container es ítem flex del hero: sin esto, en móvil se ensancha más que el visor */
.e404 > .e404-container { min-width: 0; max-width: min(100%, var(--maxw)); }
.e404__grid {
    display: grid;
    grid-template-columns: minmax(0, 1.05fr) minmax(0, .95fr);
    align-items: center;
    gap: clamp(32px, 5vw, 72px);
    padding-block: clamp(48px, 7vw, 96px);
}

/* ---- Columna de texto ---- */
.e404__title {
    font-family: inherit;
    font-size: clamp(38px, 4.6vw, 66px);
    text-wrap: balance;
    line-height: .98;
    letter-spacing: -.035em;
    font-weight: 700;
    color: var(--ink);
}
.e404__accent {
    display: block;
    background-image: linear-gradient(90deg, #ff8414, #f26b05);
    -webkit-background-clip: text;
            background-clip: text;
    padding: .06em .08em .16em;
    margin: -.06em -.08em -.16em;
    color: transparent;
}
.e404__desc {
    margin-top: 22px;
    max-width: 540px;
    font-size: clamp(15px, 1.4vw, 17px);
    color: var(--muted);
}
.e404__desc b { color: var(--ink); font-weight: 600; }

.e404__cta {
    margin-top: 30px;
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
}

/* =========================================================
   ESCENA: LA PUERTA 404
   ========================================================= */
.e404__stage { display: flex; justify-content: center; }
.scene { position: relative; width: min(100%, 300px); }

/* Contenedor con perspectiva: aquí vive la ilusión 3D de la hoja */
.door-scene {
    position: relative;
    perspective: 1400px;
    cursor: pointer;
    outline: none;
    -webkit-tap-highlight-color: transparent;
}
.door-scene:focus-visible .wall { box-shadow: 0 0 0 4px rgba(109, 60, 224, .25), var(--shadow); }

/* Pared: el bloque de vidrio que hace de tarjeta */
.wall {
    position: relative;
    padding: 26px 26px 32px;
    border-radius: 28px;
    background: linear-gradient(180deg, rgba(255, 255, 255, .9), rgba(255, 255, 255, .62)), var(--surface);
    border: 1px solid rgba(255, 255, 255, .9);
    box-shadow: var(--shadow), inset 0 1px 0 rgba(255, 255, 255, .9);
    transition: box-shadow .3s var(--ease);
}
/* Rodapié de la pared */
.wall::after {
    content: "";
    position: absolute;
    left: 0; right: 0; bottom: 0;
    height: 22px;
    border-radius: 0 0 28px 28px;
    background: linear-gradient(180deg, rgba(8, 11, 15, .03), rgba(8, 11, 15, .08));
    pointer-events: none;
}

/* Placa con el número */
.plaque {
    position: absolute;
    z-index: 3;
    top: 22px;
    left: 50%;
    translate: -50% 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 8px 18px 9px;
    border-radius: 12px;
    background: var(--ink);
    color: #fff;
    box-shadow: 0 10px 24px -12px rgba(8, 11, 15, .7), inset 0 1px 0 rgba(255, 255, 255, .18);
}
.plaque__num {
    font-size: 30px;
    font-weight: 800;
    line-height: 1;
    letter-spacing: .06em;
    background-image: linear-gradient(96deg, var(--grad-c), #ff9b5e);
    -webkit-background-clip: text; background-clip: text;
    color: transparent;
}
.plaque__sub {
    margin-top: 3px;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: .24em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, .55);
}

/* Marco: contiene el vacío de atrás y la hoja delante */
.frame {
    position: relative;
    margin-top: 58px;
    aspect-ratio: 1 / 1.95;
    border-radius: 14px 14px 6px 6px;
    padding: 10px 10px 0;
    background: linear-gradient(180deg, #ffffff, #efe8e0);
    box-shadow: inset 0 0 0 1px rgba(8, 11, 15, .08), 0 12px 30px -18px rgba(8, 11, 15, .45);
    transform-style: preserve-3d;
}

/* Lo que hay detrás de la puerta: un vacío iluminado con el degradado de marca */
.void {
    position: absolute;
    top: 10px; right: 10px; bottom: 0; left: 10px;
    border-radius: 8px 8px 0 0;
    overflow: hidden;
    background: radial-gradient(120% 80% at 50% 100%, #2a1550, var(--ink) 70%);
    display: grid;
    place-items: center;
    /* La hoja abierta sigue tapando el tercio izquierdo: el texto va a la derecha */
    padding-left: 34%;
}
.void__glow {
    position: absolute;
    top: -30%; right: -30%; bottom: -30%; left: -30%;
    background:
        radial-gradient(40% 40% at 50% 60%, rgba(242, 104, 31, .55), transparent 70%),
        radial-gradient(50% 50% at 30% 30%, rgba(109, 60, 224, .5), transparent 70%),
        radial-gradient(45% 45% at 75% 35%, rgba(184, 57, 159, .45), transparent 70%);
    opacity: 0;
    transition: opacity .6s var(--ease) .2s;
}
@keyframes void-drift {
    from { rotate: 0deg;  scale: 1; }
    to   { rotate: 20deg; scale: 1.15; }
}
.void__text {
    position: relative;
    color: #fff;
    font-weight: 700;
    font-size: clamp(20px, 2.2vw, 26px);
    line-height: 1.05;
    letter-spacing: -.02em;
    text-align: center;
    opacity: 0;
    translate: 0 8px;
    transition: opacity .5s var(--ease) .35s, translate .5s var(--ease) .35s;
}

/* La hoja de la puerta */
.door {
    position: absolute;
    top: 10px; right: 10px; bottom: 0; left: 10px;
    border-radius: 8px 8px 0 0;
    background:
        repeating-linear-gradient(90deg, rgba(255, 255, 255, .35) 0 2px, transparent 2px 9px),
        linear-gradient(180deg, var(--wood-1), var(--wood-2));
    box-shadow: inset 0 0 0 1px rgba(8, 11, 15, .06), inset -6px 0 12px -8px rgba(8, 11, 15, .18);
    transform-origin: left center;
    transform: rotateY(0deg);
    transition: transform .9s var(--ease), box-shadow .9s var(--ease);
    backface-visibility: hidden;
    will-change: transform;
}
.door__panel {
    position: absolute;
    left: 14%; right: 14%;
    border-radius: 6px;
    box-shadow: inset 0 0 0 1.5px var(--wood-line), inset 0 2px 0 rgba(255, 255, 255, .7), inset 0 -2px 0 rgba(8, 11, 15, .06);
}
.door__panel--top    { top: 8%;  height: 34%; }
.door__panel--bottom { top: 50%; height: 40%; }

.door__peephole {
    position: absolute;
    top: 24%;
    left: 50%;
    translate: -50% 0;
    width: 10px; height: 10px;
    border-radius: 50%;
    background: radial-gradient(circle at 40% 40%, #8ab7c9, #0f2a36 70%);
    box-shadow: 0 0 0 2px #cdbba6, 0 0 0 3px rgba(255, 255, 255, .7);
}

/* Picaporte: el único naranja de la escena en reposo */
.door__handle {
    position: absolute;
    z-index: 2;              /* pasa por delante del aro del cartel */
    top: 47%;
    right: 12%;
    width: 30px; height: 9px;
    border-radius: 6px;
    background-image: linear-gradient(115deg, var(--btn-a), var(--btn-b) 45%, var(--btn-c));
    box-shadow: 0 6px 14px -6px rgba(230, 79, 20, .8), inset 0 1px 0 rgba(255, 255, 255, .4);
    transform-origin: right center;
    transition: rotate .3s var(--ease);
}
.door__handle::before {
    content: "";
    position: absolute;
    right: -4px; top: -4px;
    width: 18px; height: 18px;
    border-radius: 50%;
    background: radial-gradient(circle at 35% 35%, #ff9b5e, var(--btn-b) 60%, var(--btn-a));
    box-shadow: 0 4px 10px -4px rgba(193, 34, 11, .7);
}

/* Cartel "Sin disponibilidad" colgado de la barra del picaporte.
   El pivote del balanceo es el centro del aro. */
.hanger {
    position: absolute;
    z-index: 1;
    top: calc(47% + 4.5px - 15px);
    right: calc(12% + 20px - 42px);
    transform-origin: 50% 15px;
    animation: hanger-swing 4.2s ease-in-out infinite;
    transition: rotate .4s var(--ease);
}
@keyframes hanger-swing {
    0%, 100% { rotate: -5deg; }
    50%      { rotate: 6deg; }
}
.hanger__ring {
    display: block;
    width: 30px; height: 30px;
    margin: 0 auto -4px;
    position: relative;
    z-index: 1;
    border: 3px solid #cdbba6;
    border-radius: 50%;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, .6), 0 1px 2px rgba(8, 11, 15, .15);
}
.hanger__card {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 84px;
    padding: 3px 8px 10px;
    border-radius: 10px;
    background: #fff;
    border: 1px solid var(--line);
    box-shadow: 0 10px 22px -12px rgba(8, 11, 15, .5);
    text-align: center;
    line-height: 1.05;
}
.hanger__card::before {
    content: "";
    width: 3px; height: 3px;
    margin-bottom: 9px;
    border-radius: 50%;
    background: var(--surface);
    box-shadow: inset 0 0 0 1px var(--line-strong);
}
.hanger__card b {
    font-size: 11px;
    font-weight: 800;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: var(--ink);
}
.hanger__card b + b { color: var(--btn-b); }
.hanger__card small {
    margin-top: 6px;
    font-size: 8.5px;
    font-weight: 600;
    letter-spacing: .1em;
    text-transform: uppercase;
    color: var(--muted);
}

/* Felpudo */
.mat {
    display: block;
    margin: 0 auto;
    width: 62%;
    padding: 7px 0;
    border-radius: 4px;
    background: repeating-linear-gradient(45deg, rgba(8, 11, 15, .06) 0 3px, transparent 3px 7px), #d9cbb9;
    color: #7a6754;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: .3em;
    text-align: center;
    transform: perspective(300px) rotateX(48deg);
    transform-origin: top center;
    box-shadow: 0 8px 14px -10px rgba(8, 11, 15, .5);
}

/* ---- Estados de la puerta ---- */
/* Golpe: la hoja vibra un instante */
.door-scene.is-knocking .door { animation: door-knock .5s var(--ease); }
@keyframes door-knock {
    0%, 100% { translate: 0 0; }
    18%      { translate: -3px 0; }
    36%      { translate: 3px 0; }
    54%      { translate: -2px 0; }
    72%      { translate: 2px 0; }
}
.knock { position: absolute; top: 44%; right: 4%; pointer-events: none; }
.knock i {
    position: absolute;
    right: 0;
    font-style: normal;
    font-weight: 800;
    font-size: 15px;
    letter-spacing: .04em;
    color: var(--ink);
    opacity: 0;
}
.knock i:nth-child(2) { right: 22px; top: -16px; }
.door-scene.is-knocking .knock i { animation: knock-pop .7s var(--ease) forwards; }
.door-scene.is-knocking .knock i:nth-child(2) { animation-delay: .16s; }
@keyframes knock-pop {
    0%   { opacity: 0; translate: 0 6px; scale: .7; }
    30%  { opacity: 1; translate: 0 -6px; scale: 1.1; }
    100% { opacity: 0; translate: 0 -30px; scale: 1; }
}

/* Abierta: solo con clic (o Enter / espacio) */
.door-scene.is-open .door {
    transform: rotateY(-62deg);
    box-shadow: inset 0 0 0 1px rgba(8, 11, 15, .06), 18px 0 40px -14px rgba(8, 11, 15, .55);
}
.door-scene.is-open .door__handle { rotate: 18deg; }
/* El resplandor solo se mueve con la puerta abierta (cerrada no se ve). */
.door-scene.is-open .void__glow { opacity: 1; animation: void-drift 9s ease-in-out infinite alternate; }
.door-scene.is-open .void__text { opacity: 1; translate: 0 0; }
.door-scene.is-open .hanger { animation-play-state: paused; rotate: 14deg; }

/* =========================================================
   SOPHI — chat flotante
   ========================================================= */
.sophi {
    position: fixed;
    z-index: 90;
    right: 20px;
    bottom: 20px;
    width: min(330px, calc(100vw - 40px));
    border-radius: 18px;
    overflow: hidden;
    background: var(--chat-bg);
    box-shadow: 0 24px 60px -20px rgba(8, 11, 15, .45);
    opacity: 0;
    translate: 0 18px;
    transition: opacity .45s var(--ease), translate .45s var(--ease);
}
.sophi.is-in { opacity: 1; translate: 0 0; }
.sophi__bar {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    background: var(--chat-bar);
    color: #fff;
}
.sophi__avatar {
    width: 32px; height: 32px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    font-weight: 800;
    font-size: 14px;
    color: #fff;
    background-image: linear-gradient(135deg, var(--grad-a), var(--grad-b), var(--grad-c));
}
.sophi__who { display: flex; flex-direction: column; line-height: 1.1; flex: 1; }
.sophi__who b { font-size: 14px; }
.sophi__who small { font-size: 11px; color: #8fd3c0; }
/* Cerrar: grande y con fondo propio para que se vea sobre la barra oscura */
.nf .sophi__close {
    flex: none;
    width: 38px; height: 38px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    color: #fff;
    background: rgba(255, 255, 255, .16);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, .28);
    transition: background .2s, box-shadow .2s, transform .2s;
}
.nf .sophi__close:hover { background: rgba(255, 255, 255, .3); transform: scale(1.06); }
.nf .sophi__close:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
.sophi__close svg { width: 20px; height: 20px; }
.sophi__log {
    padding: 14px 12px 6px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-height: 60px;
}
.msg {
    align-self: flex-start;
    max-width: 92%;
    padding: 8px 12px;
    border-radius: 12px 12px 12px 4px;
    background: var(--chat-in);
    font-size: 13.5px;
    line-height: 1.4;
    color: var(--ink);
    box-shadow: 0 2px 6px -4px rgba(8, 11, 15, .4);
    animation: msg-in .35s var(--ease);
}
.msg small {
    display: block;
    margin-top: 3px;
    font-size: 10px;
    color: #8a969c;
    text-align: right;
}
@keyframes msg-in {
    from { opacity: 0; translate: 0 6px; }
    to   { opacity: 1; translate: 0 0; }
}
.msg--typing { display: inline-flex; gap: 4px; padding: 11px 14px; }
.msg--typing i,
.sophi-launcher__teaser i {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: #9aa5aa;
    animation: typing 1s ease-in-out infinite;
}
.msg--typing i:nth-child(2), .sophi-launcher__teaser i:nth-child(2) { animation-delay: .15s; }
.msg--typing i:nth-child(3), .sophi-launcher__teaser i:nth-child(3) { animation-delay: .3s; }
@keyframes typing {
    0%, 60%, 100% { translate: 0 0; opacity: .5; }
    30%           { translate: 0 -4px; opacity: 1; }
}
.sophi__cta {
    margin: 8px 12px 12px;
    justify-content: center;
    --pad-y: 11px;
    font-size: 14px;
    display: none;
}
.sophi.has-cta .sophi__cta { display: inline-flex; width: calc(100% - 24px); }

/* ---- Lanzador de Sophi ---- */
.nf .sophi-launcher {
    position: fixed;
    z-index: 90;
    right: 20px;
    bottom: 20px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 10px;
    text-align: left;
    opacity: 0;
    translate: 0 14px;
    transition: opacity .45s var(--ease), translate .45s var(--ease);
}
.nf .sophi-launcher.is-in { opacity: 1; translate: 0 0; }
.sophi-launcher__teaser {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: min(300px, calc(100vw - 40px));
    padding: 10px 14px;
    border-radius: 14px 14px 4px 14px;
    background: #fff;
    border: 1px solid var(--line);
    box-shadow: 0 14px 30px -16px rgba(8, 11, 15, .45);
    font-size: 13.5px;
    line-height: 1.35;
    color: var(--ink);
    animation: msg-in .35s var(--ease);
}
.sophi-launcher__btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 6px 16px 6px 6px;
    border-radius: var(--radius-pill);
    background: var(--chat-bar);
    color: #fff;
    font-size: 14px;
    box-shadow: 0 14px 30px -14px rgba(8, 11, 15, .6);
    transition: transform .25s var(--ease), box-shadow .25s var(--ease);
}
.sophi-launcher:hover .sophi-launcher__btn {
    transform: translateY(-2px);
    box-shadow: 0 18px 34px -14px rgba(8, 11, 15, .7);
}
.sophi-launcher__badge {
    position: absolute;
    top: -5px; right: -3px;
    min-width: 18px; height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    display: grid;
    place-items: center;
    font-style: normal;
    font-size: 11px;
    font-weight: 800;
    background-image: linear-gradient(115deg, var(--btn-b), var(--btn-c));
    box-shadow: 0 0 0 2px #fff;
    opacity: 0;
    scale: .6;
    transition: opacity .3s var(--ease), scale .3s var(--ease);
}
.sophi-launcher.has-unread .sophi-launcher__badge { opacity: 1; scale: 1; }

/* ---------- Entrada ---------- */
.e404-reveal {
    opacity: 0;
    transform: translateY(28px);
    transition: opacity .7s var(--ease), transform .7s var(--ease);
    transition-delay: var(--delay, 0ms);
    will-change: opacity, transform;
}
.e404-reveal.in { opacity: 1; transform: none; }

@media (prefers-reduced-motion: reduce) {
    .e404-reveal { opacity: 1 !important; transform: none !important; }
    .e404-btn--llama, .e404-btn--llama::after, .e404__aura,
    .hanger, .void__glow, .msg--typing i, .sophi-launcher__teaser i { animation: none !important; }
    .door, .e404__aura { transition: none !important; }
}

/* ---------- Responsive ---------- */
@media (min-width: 901px) {
    /* min-height y no height: en idiomas con textos más largos el hero crece en vez de recortarse */
    .e404 { min-height: calc(100vh - var(--e404-header)); min-height: calc(100dvh - var(--e404-header)); }
    .e404__grid { padding-block: 0; }
}
@media (min-width: 901px) and (max-height: 820px) {
    .scene { width: min(100%, 250px); }
    .frame { aspect-ratio: 1 / 1.8; }
    .wall { padding: 22px 22px 28px; }
    .e404__grid { padding-block: clamp(24px, 4vw, 56px); }
}
@media (max-width: 1100px) {
    .e404__title { font-size: clamp(38px, 5.4vw, 60px); }
}
@media (max-width: 900px) {
    .e404 { min-height: 0; }
    .e404__grid {
        grid-template-columns: minmax(0, 1fr);
        gap: 48px;
        padding-top: clamp(24px, 6vw, 48px);
    }
    .e404__stage { order: 2; }
    .e404__watermark { font-size: 46vw; right: -6vw; bottom: 6%; }
    .scene { width: min(100%, 300px); }
}
@media (max-width: 600px) {
    .sophi { right: 12px; bottom: 12px; left: 12px; width: auto; }
    .nf .sophi-launcher { right: 12px; bottom: 12px; }
    .e404__cta .e404-btn { flex: 1 1 auto; justify-content: center; white-space: nowrap; }
}
</style>
